import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, ShieldCheck } from "lucide-react";
import { StarRating, TrustScore } from "./StarRating";
import { api } from "../lib/api";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
};

export default function ReviewsSection({ variant = "home", title, kicker = "Anmeldelser" }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const isHome = variant === "home";

  React.useEffect(() => {
    let cancelled = false;
    api.get("/reviews/public", { params: { limit: isHome ? 6 : 50 } })
      .then(({ data }) => { if (!cancelled) { setData(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setLoading(false); } });
    return () => { cancelled = true; };
  }, [isHome]);

  const items = data?.items || [];
  const stats = data?.stats || { count: 0, average: 0, distribution: {} };
  const heading = title || (stats.count > 0 ? "Hvad vores kunder siger" : "Vær blandt de første til at anmelde os");

  return (
    <section className="bg-brand-bg2 border-y border-brand py-20 lg:py-28" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Sidebar score */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24">
            <div className="kicker">{kicker}</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight mt-3 text-brand-dark">
              {heading}
            </h2>

            <div className="bg-white border border-brand p-6 mt-7" data-testid="trust-card">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold text-5xl text-brand-dark leading-none">
                    {stats.average || "—"}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-brand-text2 mt-2">Trust-score / 5</div>
                </div>
                <ShieldCheck className="text-[#00B67A]" size={36} />
              </div>
              <div className="mt-5"><StarRating value={Math.round(stats.average)} size={22} /></div>
              <div className="text-sm text-brand-text2 mt-3">
                {stats.count > 0
                  ? <>Baseret på <strong className="text-brand-dark">{stats.count}</strong> verificerede {stats.count === 1 ? "anmeldelse" : "anmeldelser"}</>
                  : "Verificerede anmeldelser fra rigtige kunder"}
              </div>

              {/* Distribution bars */}
              <div className="mt-6 space-y-2">
                {[5, 4, 3, 2, 1].map((n) => {
                  const c = stats.distribution?.[n] || 0;
                  const pct = stats.count ? Math.round((c / stats.count) * 100) : 0;
                  return (
                    <div key={n} className="flex items-center gap-3 text-xs">
                      <div className="font-mono text-brand-text2 w-3">{n}</div>
                      <div className="flex-1 h-1.5 bg-slate-200 overflow-hidden">
                        <div className="h-full bg-[#00B67A]" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="font-mono text-brand-text2 w-8 text-right">{c}</div>
                    </div>
                  );
                })}
              </div>

              <Link to="/anmeldelser" data-testid="reviews-cta-write" className="btn-primary w-full justify-center mt-7">
                Skriv en anmeldelse <ArrowRight size={16} />
              </Link>
            </div>
          </aside>

          {/* Reviews list */}
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {loading && Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white border border-brand p-6 h-44 animate-pulse" />
            ))}
            {!loading && items.length === 0 && (
              <div className="sm:col-span-2 bg-white border border-brand p-10 text-center" data-testid="reviews-empty">
                <Quote className="text-brand-red mx-auto" size={28} />
                <h3 className="font-display font-semibold text-2xl text-brand-dark mt-4">Bliv den første til at anmelde os</h3>
                <p className="text-brand-text2 mt-2 max-w-md mx-auto">Har du brugt EMA Kurer? Vi vil meget gerne høre din mening — og din anmeldelse hjælper andre med at vælge sikkert.</p>
                <Link to="/anmeldelser" className="btn-secondary mt-6 inline-flex">Skriv en anmeldelse <ArrowRight size={16} /></Link>
              </div>
            )}
            {!loading && items.map((r, i) => (
              <article key={r.id || i} className="bg-white border border-brand p-6 flex flex-col" data-testid={`review-${i}`}>
                <div className="flex items-center justify-between">
                  <StarRating value={r.rating} size={16} />
                  <span className="text-[10px] uppercase tracking-widest text-brand-text2">{fmt(r.created_at)}</span>
                </div>
                <h4 className="font-display font-semibold text-lg text-brand-dark mt-3">{r.title}</h4>
                <p className="text-brand-text2 text-sm mt-2 leading-relaxed flex-1 line-clamp-5">{r.body}</p>
                <div className="border-t border-brand mt-5 pt-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-brand-dark text-white flex items-center justify-center text-xs font-bold">
                    {(r.display_name || "A").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-brand-dark">{r.display_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#00B67A] font-semibold flex items-center gap-1">
                      <ShieldCheck size={10} /> Verificeret kunde
                    </div>
                  </div>
                </div>
              </article>
            ))}
            {!loading && items.length > 0 && (
              <div className="sm:col-span-2 flex justify-center mt-2">
                <Link to="/anmeldelser" data-testid="reviews-see-all" className="btn-secondary">
                  Se alle anmeldelser <ArrowRight size={16} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export { TrustScore };
